import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  Shield,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  RefreshCw,
  Search,
  Users,
  FileWarning,
  Loader2,
  ExternalLink,
  UserCheck,
  Ban,
} from 'lucide-react';

/**
 * LicenceComplianceWidget - Dashboard component for NSW SLED compliance
 * P0 FIX: Now properly displays verification status tied to officer profiles
 */
export function LicenceComplianceWidget({ compact = false }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [compliance, setCompliance] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [licenceNumber, setLicenceNumber] = useState('');
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  
  // P0 FIX: Officer verification state
  const [verifyingOfficerId, setVerifyingOfficerId] = useState(null);
  const [showOfficersList, setShowOfficersList] = useState(null); // 'missing', 'expired', 'expiring', 'suspended', 'valid'

  const fetchCompliance = async () => {
    try {
      setLoading(true);
      const response = await api.get('/sled/compliance');
      setCompliance(response.data);
    } catch (error) {
      console.error('Failed to fetch compliance data:', error);
      toast.error('Failed to load licence compliance data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCompliance();
    // Refresh every 5 minutes
    const interval = setInterval(fetchCompliance, 300000);
    return () => clearInterval(interval);
  }, []);

  // P0 FIX: Verify a specific officer's licence and refresh compliance
  const handleVerifyOfficer = async (officerId) => {
    setVerifyingOfficerId(officerId);
    
    try {
      const response = await api.post(`/sled/verify-officer/${officerId}?force_refresh=true`);
      
      if (response.data.success) {
        toast.success(response.data.message || 'Licence verified successfully');
        
        // Update compliance data immediately from response
        if (response.data.compliance_summary) {
          setCompliance(response.data.compliance_summary);
        } else {
          // Fallback: refetch compliance data
          await fetchCompliance();
        }
      } else {
        toast.error(response.data.message || 'Verification failed');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to verify licence';
      toast.error(errorMsg);
    } finally {
      setVerifyingOfficerId(null);
    }
  };

  // Force refresh from authority (bypasses cache)
  const handleForceRefresh = async () => {
    if (!licenceNumber.trim()) {
      toast.error('Please enter a licence number');
      return;
    }
    setVerifyLoading(true);
    setVerifyResult(null);
    try {
      const response = await api.get(`/sled/verify/${encodeURIComponent(licenceNumber.trim())}?force_refresh=true`);
      setVerifyResult(response.data);
      if (response.data.is_valid) {
        toast.success('Licence verified directly from authority');
      } else {
        toast.warning(`Licence status: ${response.data.status}`);
      }
    } catch (error) {
      toast.error('Failed to refresh from authority');
      setVerifyResult({ error: error.response?.data?.detail || 'Authority verification failed' });
    } finally {
      setVerifyLoading(false);
    }
  };

  const handleVerifyLicence = async () => {
    if (!licenceNumber.trim()) {
      toast.error('Please enter a licence number');
      return;
    }
    
    setVerifyLoading(true);
    setVerifyResult(null);
    
    try {
      const response = await api.get(`/sled/verify/${encodeURIComponent(licenceNumber.trim())}`);
      setVerifyResult(response.data);
      
      if (response.data.is_valid) {
        toast.success('Licence is valid and current');
      } else {
        toast.warning(`Licence status: ${response.data.status}`);
      }
    } catch (error) {
      toast.error('Failed to verify licence');
      setVerifyResult({ error: error.response?.data?.detail || 'Verification failed' });
    } finally {
      setVerifyLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-zinc-400" />
        </CardContent>
      </Card>
    );
  }

  if (!compliance) {
    return (
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="text-center py-8 text-zinc-500">
          <Shield className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>Failed to load compliance data</p>
        </CardContent>
      </Card>
    );
  }

  const hasIssues = compliance.expired > 0 || compliance.suspended > 0;
  const hasWarnings = compliance.expiring_soon > 0 || compliance.not_verified > 0;

  // P0 FIX: Render officer list with verify buttons
  const renderOfficersList = (officers, type) => {
    if (!officers || officers.length === 0) {
      return <p className="text-zinc-500 text-sm">No officers in this category</p>;
    }
    
    return (
      <div className="space-y-2 max-h-80 overflow-y-auto">
        {officers.map(officer => (
          <div 
            key={officer.id} 
            className="flex items-center justify-between p-3 bg-[#09090b] rounded-lg border border-[#3f3f46]"
          >
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{officer.name}</p>
              <p className="text-xs text-zinc-500 truncate">{officer.email}</p>
              {officer.licence_number && (
                <p className="text-xs text-zinc-400">Licence: {officer.licence_number}</p>
              )}
              {officer.days_until_expiry !== undefined && (
                <p className="text-xs text-amber-400">{officer.days_until_expiry} days until expiry</p>
              )}
              {officer.days_overdue !== undefined && (
                <p className="text-xs text-red-400">{officer.days_overdue} days overdue</p>
              )}
              {officer.verified_at && (
                <p className="text-xs text-zinc-500">
                  Last verified: {new Date(officer.verified_at).toLocaleDateString('en-AU')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              {type === 'missing' ? (
                <>
                  <Badge className="bg-zinc-500/20 text-zinc-400 border border-zinc-500/30">
                    No Licence
                  </Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => window.location.href = `/users?edit=${officer.id}`}
                    className="border-blue-500/50 text-blue-400 hover:bg-blue-500/10"
                    data-testid={`edit-officer-${officer.id}`}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Edit
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleVerifyOfficer(officer.id)}
                  disabled={verifyingOfficerId === officer.id || !officer.licence_number}
                  className="border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10"
                  data-testid={`verify-officer-${officer.id}`}
                >
                  {verifyingOfficerId === officer.id ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <UserCheck className="w-3 h-3 mr-1" />
                  )}
                  Verify
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Compact view for sidebar/widget
  if (compact) {
    return (
      <Card className={`bg-[#18181b] border-[#3f3f46] ${hasIssues ? 'border-red-500/50' : hasWarnings ? 'border-amber-500/50' : ''}`}>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-rajdhani flex items-center gap-2">
            <Shield className={`w-4 h-4 ${hasIssues ? 'text-red-400' : 'text-emerald-400'}`} />
            Licence Compliance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-400" />
              <span className="text-zinc-300">{compliance.valid_licences} Valid</span>
            </div>
            {compliance.expired > 0 && (
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-400" />
                <span className="text-red-400">{compliance.expired} Expired</span>
              </div>
            )}
            {compliance.expiring_soon > 0 && (
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400">{compliance.expiring_soon} Expiring</span>
              </div>
            )}
            {compliance.not_verified > 0 && (
              <div className="flex items-center gap-2">
                <FileWarning className="w-4 h-4 text-zinc-400" />
                <span className="text-zinc-400">{compliance.not_verified} Unverified</span>
              </div>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="w-full mt-3 border-zinc-600"
            onClick={() => setShowDetails(true)}
          >
            View Details
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Full view
  return (
    <>
      <Card className={`bg-[#18181b] border-[#3f3f46] ${hasIssues ? 'border-red-500/50' : ''}`}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-rajdhani flex items-center gap-2">
              <Shield className={`w-5 h-5 ${hasIssues ? 'text-red-400' : 'text-emerald-400'}`} />
              NSW SLED Licence Compliance
            </CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setVerifyDialogOpen(true)}
                className="border-zinc-600"
              >
                <Search className="w-4 h-4 mr-1" />
                Verify Licence
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={fetchCompliance}
                className="border-zinc-600"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
            <div className="p-3 bg-[#09090b] rounded-lg border border-[#3f3f46] text-center">
              <Users className="w-5 h-5 mx-auto mb-1 text-zinc-400" />
              <p className="text-2xl font-bold text-white">{compliance.total_officers}</p>
              <p className="text-xs text-zinc-500">Total Officers</p>
            </div>
            <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30 text-center">
              <CheckCircle2 className="w-5 h-5 mx-auto mb-1 text-emerald-400" />
              <p className="text-2xl font-bold text-emerald-400">{compliance.valid_licences}</p>
              <p className="text-xs text-emerald-400/70">Valid Licences</p>
            </div>
            <div className={`p-3 rounded-lg border text-center ${compliance.expiring_soon > 0 ? 'bg-amber-500/10 border-amber-500/30' : 'bg-[#09090b] border-[#3f3f46]'}`}>
              <Clock className={`w-5 h-5 mx-auto mb-1 ${compliance.expiring_soon > 0 ? 'text-amber-400' : 'text-zinc-400'}`} />
              <p className={`text-2xl font-bold ${compliance.expiring_soon > 0 ? 'text-amber-400' : 'text-white'}`}>{compliance.expiring_soon}</p>
              <p className="text-xs text-zinc-500">Expiring (30d)</p>
            </div>
            <div className={`p-3 rounded-lg border text-center ${compliance.expired > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-[#09090b] border-[#3f3f46]'}`}>
              <XCircle className={`w-5 h-5 mx-auto mb-1 ${compliance.expired > 0 ? 'text-red-400' : 'text-zinc-400'}`} />
              <p className={`text-2xl font-bold ${compliance.expired > 0 ? 'text-red-400' : 'text-white'}`}>{compliance.expired}</p>
              <p className="text-xs text-zinc-500">Expired</p>
            </div>
            <div className={`p-3 rounded-lg border text-center ${compliance.suspended > 0 ? 'bg-red-500/10 border-red-500/30' : 'bg-[#09090b] border-[#3f3f46]'}`}>
              <AlertTriangle className={`w-5 h-5 mx-auto mb-1 ${compliance.suspended > 0 ? 'text-red-400' : 'text-zinc-400'}`} />
              <p className={`text-2xl font-bold ${compliance.suspended > 0 ? 'text-red-400' : 'text-white'}`}>{compliance.suspended}</p>
              <p className="text-xs text-zinc-500">Suspended</p>
            </div>
          </div>

          {/* Alerts */}
          {hasIssues && (
            <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400 mt-0.5" />
                <div>
                  <p className="font-medium text-red-400">Compliance Alert</p>
                  <p className="text-sm text-red-300/80 mt-1">
                    {compliance.expired} officer(s) have expired licences and {compliance.suspended} have suspended licences.
                    These officers cannot legally work under NSW SLED regulations.
                  </p>
                </div>
              </div>
            </div>
          )}

          {compliance.expiring_soon > 0 && (
            <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-400 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-400">Expiring Soon</p>
                  <p className="text-sm text-amber-300/80 mt-1">
                    {compliance.expiring_soon} officer(s) have licences expiring within 30 days.
                    Please remind them to renew.
                  </p>
                  {compliance.officers_expiring_soon?.length > 0 && (
                    <ul className="mt-2 text-sm text-amber-300/70">
                      {compliance.officers_expiring_soon.slice(0, 3).map(o => (
                        <li key={o.id}>• {o.name} - {o.days_until_expiry} days left</li>
                      ))}
                      {compliance.officers_expiring_soon.length > 3 && (
                        <li>...and {compliance.officers_expiring_soon.length - 3} more</li>
                      )}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          )}

          {compliance.not_verified > 0 && (
            <div className="p-4 bg-zinc-500/10 border border-zinc-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <FileWarning className="w-5 h-5 text-zinc-400 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-zinc-300">Missing Licence Information</p>
                  <p className="text-sm text-zinc-400 mt-1">
                    {compliance.not_verified} officer(s) do not have a security licence number on file.
                    Please update their profiles with licence information.
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2 border-zinc-600 text-zinc-300"
                    onClick={() => setShowOfficersList('missing')}
                  >
                    View Officers
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* P0 FIX: Officers Lists by Category */}
          {compliance.officers_expired?.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-red-400 flex items-center gap-2">
                  <Ban className="w-4 h-4" />
                  Expired Licences ({compliance.expired})
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowOfficersList(showOfficersList === 'expired' ? null : 'expired')}
                  className="text-zinc-400"
                >
                  {showOfficersList === 'expired' ? 'Hide' : 'Show'}
                </Button>
              </div>
              {showOfficersList === 'expired' && renderOfficersList(compliance.officers_expired, 'expired')}
            </div>
          )}

          {compliance.officers_suspended?.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-red-400 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  Suspended Licences ({compliance.suspended})
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowOfficersList(showOfficersList === 'suspended' ? null : 'suspended')}
                  className="text-zinc-400"
                >
                  {showOfficersList === 'suspended' ? 'Hide' : 'Show'}
                </Button>
              </div>
              {showOfficersList === 'suspended' && renderOfficersList(compliance.officers_suspended, 'suspended')}
            </div>
          )}

          {!hasIssues && !hasWarnings && (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-400 mt-0.5" />
                <div>
                  <p className="font-medium text-emerald-400">All Compliant</p>
                  <p className="text-sm text-emerald-300/80 mt-1">
                    All security officers have valid and current NSW security licences.
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Verify Licence Dialog */}
      <Dialog open={verifyDialogOpen} onOpenChange={setVerifyDialogOpen}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <Search className="w-5 h-5 text-cyan-400" />
              Verify Security Licence
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">NSW Security Licence Number</Label>
              <Input
                value={licenceNumber}
                onChange={(e) => setLicenceNumber(e.target.value.toUpperCase())}
                placeholder="e.g., 123456789"
                className="bg-[#09090b] border-[#3f3f46] text-white"
                data-testid="licence-number-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-2">
              <Button
                className="bg-cyan-500 hover:bg-cyan-600"
                onClick={handleVerifyLicence}
                disabled={verifyLoading}
                data-testid="verify-licence-btn"
              >
                {verifyLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Shield className="w-4 h-4 mr-2" />
                )}
                Verify
              </Button>
              <Button
                variant="outline"
                className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                onClick={handleForceRefresh}
                disabled={verifyLoading}
                data-testid="force-refresh-btn"
              >
                {verifyLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <RefreshCw className="w-4 h-4 mr-2" />
                )}
                Refresh from Authority
              </Button>
            </div>
            
            {verifyResult && (
              <div className={`p-4 rounded-lg border ${verifyResult.is_valid ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-red-500/10 border-red-500/30'}`} data-testid="verify-result">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {verifyResult.is_valid ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                    <span className={`font-medium ${verifyResult.is_valid ? 'text-emerald-400' : 'text-red-400'}`}>
                      {verifyResult.status || 'Error'}
                    </span>
                  </div>
                  {/* Freshness Badge */}
                  {verifyResult.freshness && (
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${
                      verifyResult.freshness === 'LIVE' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' :
                      verifyResult.freshness === 'CACHED' ? 'bg-blue-500/20 text-blue-300 border-blue-500/40' :
                      'bg-red-500/20 text-red-300 border-red-500/40 animate-pulse'
                    }`} data-testid="freshness-badge">
                      {verifyResult.freshness}
                    </span>
                  )}
                </div>
                
                {!verifyResult.error && (
                  <div className="space-y-1 text-sm text-zinc-400">
                    {verifyResult.holder_name && (
                      <p>Holder: {verifyResult.holder_name}</p>
                    )}
                    {verifyResult.licence_class?.length > 0 && (
                      <p>Class: {verifyResult.licence_class.join(', ')}</p>
                    )}
                    {verifyResult.expiry_date && (
                      <p>Expiry: {new Date(verifyResult.expiry_date).toLocaleDateString('en-AU')}</p>
                    )}
                    {verifyResult.days_until_expiry != null && (
                      <p className={verifyResult.days_until_expiry < 0 ? 'text-red-400 font-medium' : verifyResult.days_until_expiry <= 30 ? 'text-amber-400' : ''}>
                        {verifyResult.days_until_expiry < 0 ? `Expired ${Math.abs(verifyResult.days_until_expiry)} days ago` : `${verifyResult.days_until_expiry} days until expiry`}
                      </p>
                    )}
                    {/* Verification metadata */}
                    {verifyResult.last_verified_at && (
                      <p className="text-xs text-zinc-500 mt-2">
                        Last verified: {new Date(verifyResult.last_verified_at).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        {verifyResult.verification_source && ` (${verifyResult.verification_source})`}
                      </p>
                    )}
                  </div>
                )}
                
                {/* Compliance Safeguard Warning */}
                {(!verifyResult.is_valid || verifyResult.freshness === 'STALE') && !verifyResult.error && (
                  <div className="mt-3 p-2 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-2" data-testid="compliance-warning">
                    <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <p className="text-red-300 font-medium">Compliance Warning</p>
                      <p className="text-red-400/80">
                        {!verifyResult.is_valid ? 'This licence is not valid. Shift allocation may be blocked.' :
                         'Verification data is stale. Refresh from authority recommended before allocating shifts.'}
                      </p>
                    </div>
                  </div>
                )}
                
                {verifyResult.error && (
                  <p className="text-sm text-red-400">{verifyResult.error}</p>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* P0 FIX: Officers List Dialog */}
      <Dialog open={showOfficersList === 'missing'} onOpenChange={() => setShowOfficersList(null)}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileWarning className="w-5 h-5 text-zinc-400" />
              Missing Licence Information
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              These officers need their security licence number added to their profile before verification can occur.
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            {renderOfficersList(compliance?.officers_missing, 'missing')}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default LicenceComplianceWidget;
